import React, { useState } from 'react';
import { useLatest, useRequest } from 'ahooks';

import { verifyProviderByLast4 } from '../../../../utils/serviceCalls/provider';
import { isValidLast4Format } from '../../../../utils/validation';
import {
  CheckIcon,
  LoadingIndicator,
} from '../../../Shared/SearchCombobox/Icons';
import { VALIDATION_MESSAGES } from '../../constants';
import { FIELDS, INPUT_WITH_ICON_PADDING_CLASSES } from '../constants';
import FieldWrapper from './FieldWrapper';
import { useFormContextSafe, useFormField } from './hooks';

/**
 * Verifies Last 4 digits against the API.
 *
 * @param {string|null} pwid - The provider's PWID for verification
 * @param {string} last4 - Last 4 digits to verify
 * @param {boolean} disabled - Skips validation when field is disabled
 * @returns {{ loading: boolean, apiErrorRef: React.RefObject<string | null> }}
 */
function useLast4ServerValidation(pwid, last4, disabled) {
  const { setValue, trigger } = useFormContextSafe();

  const [apiErrorMessage, setApiErrorMessage] = useState(
    /** @type {string | null} */ (null),
  );

  const shouldVerify =
    !disabled && !!pwid && !!last4 && isValidLast4Format(last4);

  /** Sets the verified value in form state */
  const setVerifiedLast4 = (value) => {
    setValue(FIELDS.verifiedLast4, value, { shouldValidate: false });
  };

  const { loading } = useRequest(
    async () => {
      if (!pwid || !last4) return;

      const result = await verifyProviderByLast4(pwid, last4);

      if (result.success) {
        setApiErrorMessage(null);
        setVerifiedLast4(last4);
      } else {
        setApiErrorMessage(VALIDATION_MESSAGES.LAST4_MISMATCH);
        setVerifiedLast4('');
      }
    },
    {
      refreshDeps: [pwid, last4],
      debounceWait: 300,
      ready: shouldVerify,
      onBefore: () => {
        setApiErrorMessage(null);
        setVerifiedLast4('');
      },
      onError: () => {
        setApiErrorMessage(VALIDATION_MESSAGES.LAST4_VERIFICATION_FAILED);
        setVerifiedLast4('');
      },
      onFinally: () => {
        trigger(FIELDS.last4);
      },
    },
  );

  const effectiveApiError = shouldVerify && !loading ? apiErrorMessage : null;

  const apiErrorRef = useLatest(effectiveApiError);

  return {
    loading,
    apiErrorRef,
  };
}

/**
 * A self-contained Last 4 input field that:
 * - Registers itself with the parent form via `useFormContext`
 * - Validates format (4 alphanumeric characters)
 * - Verifies against API with debouncing
 * - Shows loading indicator and checkmark on success
 * - Sets `verifiedLast4` field when API confirms validity
 *
 * @example
 * <Last4Field disabled={false} pwid="ABC123" />
 *
 * @param {Object} props
 * @param {boolean} props.disabled - Whether the field is disabled
 * @param {string} props.pwid - The provider's PWID for verification
 */
export default function Last4Field({ disabled, pwid }) {
  const {
    register,
    validateField,
    errorMessage,
    handleKeyDown,
    currentValue,
    setValue,
    watch,
  } = useFormField(FIELDS.last4, disabled);

  const verifiedLast4Value = (watch(FIELDS.verifiedLast4) || '').trim();

  const { loading, apiErrorRef } = useLast4ServerValidation(
    pwid,
    currentValue || '',
    disabled,
  );

  // Step 1: Our basic checks passed so we can verify with async API call
  const hasRequiredData = pwid && currentValue;
  const isFormatValid = isValidLast4Format(currentValue);
  const shouldVerify = !disabled && hasRequiredData && isFormatValid;

  // Step 2: Did verification succeed?
  // If the user has typed something else, we don't consider it verified.
  const doesVerifiedMatchCurrentInput =
    verifiedLast4Value === String(currentValue || '').trim();
  const isVerified =
    shouldVerify &&
    !loading &&
    verifiedLast4Value &&
    doesVerifiedMatchCurrentInput;

  // Register input with validation that checks BOTH format AND API errors
  const { onChange: rhfOnChange, ...restRegistration } = register(
    FIELDS.last4,
    {
      validate: (val = '') => {
        if (disabled) return true;
        if (!val) return VALIDATION_MESSAGES.LAST4_REQUIRED;
        if (!isValidLast4Format(val)) return VALIDATION_MESSAGES.LAST4_INVALID;
        if (apiErrorRef.current) return apiErrorRef.current;
        // Keep the field invalid until the API verification succeeds.
        // Return an empty string so the form is invalid, but we don't show an error message.
        if (!isVerified) return '';
        return true;
      },
    },
  );

  // Validate on every keystroke
  const handleChange = (e) => {
    // Any change invalidates the previous verification.
    // `verifiedLast4` is only set again after API success.
    setValue(FIELDS.verifiedLast4, '', { shouldValidate: false });
    rhfOnChange(e);
    validateField();
  };

  return (
    <FieldWrapper label="Last 4 of DEA or License" errorMessage={errorMessage}>
      <div className="relative">
        <input
          {...restRegistration}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          type="text"
          maxLength={4}
          placeholder="Enter last 4 characters"
          disabled={disabled}
          className={INPUT_WITH_ICON_PADDING_CLASSES}
          aria-invalid={!!errorMessage}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          {loading ? (
            <LoadingIndicator />
          ) : isVerified && !disabled ? (
            <CheckIcon className="h-5 w-5 text-green-500" />
          ) : null}
        </div>
      </div>
    </FieldWrapper>
  );
}
